import {
  DATE_FORMAT_DAY_MONTH_WEEKDAY_FNS,
  DATE_FORMAT_RANGE_FNS,
  DATE_FORMAT_SHORT_FNS,
  DEFAULT_DATE_FORMAT_FNS,
  GetGroupAttendanceAggregateResponse,
} from '@schooly/api';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format, getISODay, isAfter, isBefore, parse } from 'date-fns';

import { SESSION_ORDER, WORKING_WEEK_LENGTH } from './constants';
import { cellEntries } from './trackingPoints';
import {
  AttendanceGridCell,
  AttendanceGridCellValue,
  AttendanceGridColumn,
  AttendanceGridSession,
  AttendanceGridSessionHeader,
  AttendanceGridStudentRow,
} from './types';

export type GroupGridMembershipInterval = {
  start: string;
  end: string | null;
};

export type GroupGridMember = {
  relation_id: string;
  given_name: string | null;
  last_name: string;
  known_as: string | null;
  profile_picture: string | null;
  memberships: GroupGridMembershipInterval[];
};

export type BuildGroupGridInput = {
  weekDates: string[];
  today: string;
  members: GroupGridMember[];
  aggregate: GetGroupAttendanceAggregateResponse;
};

export type GroupGridResult = {
  columns: AttendanceGridColumn[];
  rows: AttendanceGridStudentRow[];
};

export type AggregateWeekDay = GetGroupAttendanceAggregateResponse['total']['week_days'][number];
export type AggregateRow = GetGroupAttendanceAggregateResponse['rows'][number];
export type AggregateCell = AggregateRow['cells'][number];
export type AggregateCellValue = AggregateCell['value'];

export const EMPTY_CELL_VALUE: AttendanceGridCellValue = {
  justification: null,
  absence_request: null,
  am: null,
  pm: null,
};

/**
 * The week grid still shows one AM and one PM slot per day, while the aggregate
 * now returns a flat `attendance_entries` list per cell (periods included).
 */
export const toGridCellValue = (value: AggregateCellValue): AttendanceGridCellValue => {
  const forSession = (session: AttendanceGridSession) => {
    const entry = cellEntries(value).find((item) => item.session === session);
    return entry ? { ...entry, absence_request: value.absence_request } : null;
  };

  return {
    justification: value.justification,
    absence_request: value.absence_request,
    am: forSession('am'),
    pm: forSession('pm'),
  };
};

export const hasSessionEntry = (value: AttendanceGridCellValue): boolean =>
  !!value.am || !!value.pm;

export const hasTrackingPointEntry = (value: AttendanceGridCellValue): boolean =>
  hasSessionEntry(value) || Object.values(value.periods ?? {}).some(Boolean);

export const parseDate = (value: string): Date =>
  parse(value.slice(0, 10), DEFAULT_DATE_FORMAT_FNS, newDateTimezoneOffset());

export const formatWeekRangeLabel = (start: string, end: string): string =>
  `${format(parseDate(start), DATE_FORMAT_SHORT_FNS)} - ${format(parseDate(end), DATE_FORMAT_RANGE_FNS)}`;

export const toSessionName = (session: string): AttendanceGridSession =>
  session === 'pm' ? 'pm' : 'am';

export const uniqueSessionNames = (sessions: AttendanceGridSession[]): AttendanceGridSession[] =>
  SESSION_ORDER.filter((session) => sessions.includes(session));

export const isDayWithinMembership = (
  day: Date,
  memberships: GroupGridMembershipInterval[],
): boolean =>
  memberships.some(({ start, end }) => {
    if (isBefore(day, parseDate(start))) return false;
    return !end || !isAfter(day, parseDate(end));
  });

const membershipOverlapsWeek = (member: GroupGridMember, weekStart: Date, weekEnd: Date): boolean =>
  member.memberships.some(({ start, end }) => {
    if (isAfter(parseDate(start), weekEnd)) return false;
    return !end || !isBefore(parseDate(end), weekStart);
  });

export const compareMembers = (a: GroupGridMember, b: GroupGridMember): number =>
  a.last_name.localeCompare(b.last_name) || (a.given_name ?? '').localeCompare(b.given_name ?? '');

/** The aggregate API keys days by ISO weekday (1 = Monday .. 7 = Sunday). */
const resolveAggregateDay = (dateKey: string): number => getISODay(parseDate(dateKey));

const indexWeekDaysByDay = (
  aggregate: GetGroupAttendanceAggregateResponse,
): Map<number, AggregateWeekDay> =>
  new Map(aggregate.total.week_days.map((weekDay) => [weekDay.day, weekDay]));

const indexRowsByRelationId = (
  aggregate: GetGroupAttendanceAggregateResponse,
): Map<string, AggregateRow> => new Map(aggregate.rows.map((row) => [row.meta.relation_id, row]));

/** Union of session names from totals and every row cell, keyed by ISO weekday. */
const indexSessionNamesByDay = (
  aggregate: GetGroupAttendanceAggregateResponse,
): Map<number, AttendanceGridSession[]> => {
  const namesByDay = new Map<number, Set<AttendanceGridSession>>();

  const add = (day: number, session: string) => {
    const names = namesByDay.get(day) ?? new Set<AttendanceGridSession>();
    names.add(toSessionName(session));
    namesByDay.set(day, names);
  };

  for (const weekDay of aggregate.total.week_days) {
    for (const point of sessionTrackingPoints(weekDay)) add(weekDay.day, String(point.session));
  }

  for (const row of aggregate.rows) {
    for (const cell of row.cells) {
      for (const session of cell.meta.sessions) add(cell.meta.day, String(session));
    }
  }

  return new Map([...namesByDay].map(([day, names]) => [day, uniqueSessionNames([...names])]));
};

/** AM/PM cells only — the week grid has no period columns. */
export const sessionCells = (cells: AggregateCell[] | undefined): AggregateCell[] =>
  (cells ?? []).filter((cell) => !cell.meta.period_id);

/** AM/PM tracking points only — the week grid has no period columns. */
export const sessionTrackingPoints = (weekDay: AggregateWeekDay | undefined) =>
  (weekDay?.tracking_points ?? []).filter((point) => !point.period && !!point.session);

export const buildColumnSessionHeaders = (
  weekDay: AggregateWeekDay | undefined,
  sessionNames: AttendanceGridSession[],
): AttendanceGridSessionHeader[] =>
  sessionNames.map((name) => {
    const sessionInfo = sessionTrackingPoints(weekDay).find(
      (point) => toSessionName(String(point.session)) === name,
    );
    const complete =
      !!sessionInfo &&
      sessionInfo.total_count > 0 &&
      sessionInfo.tracked_count === sessionInfo.total_count;
    return { name, complete };
  });

const buildColumns = (
  weekDates: string[],
  today: string,
  weekDayByAggregateDay: Map<number, AggregateWeekDay>,
  sessionNamesByDay: Map<number, AttendanceGridSession[]>,
): AttendanceGridColumn[] => {
  const todayDate = parseDate(today);

  const lastRegisteredIndex = weekDates.findLastIndex((day) =>
    weekDayByAggregateDay.has(resolveAggregateDay(day)),
  );
  const lastColumnIndex = Math.max(WORKING_WEEK_LENGTH - 1, lastRegisteredIndex);
  const columnDates = weekDates.slice(0, lastColumnIndex + 1);

  return columnDates.map((day) => {
    const aggregateDay = resolveAggregateDay(day);
    const weekDay = weekDayByAggregateDay.get(aggregateDay);
    const sessionNames = sessionNamesByDay.get(aggregateDay) ?? [];

    return {
      key: day,
      date: day,
      label: format(parseDate(day), DATE_FORMAT_DAY_MONTH_WEEKDAY_FNS).toUpperCase(),
      sessions: buildColumnSessionHeaders(weekDay, sessionNames),
      has_register: !!weekDay?.register_id,
      is_future: isAfter(parseDate(day), todayDate),
    };
  });
};

const buildDayCell = (
  column: AttendanceGridColumn,
  member: GroupGridMember,
  weekDayByAggregateDay: Map<number, AggregateWeekDay>,
  cellsByAggregateDay: Map<number, AggregateCell[]>,
): AttendanceGridCell => {
  const isFuture = !!column.is_future;

  if (!isDayWithinMembership(parseDate(column.key), member.memberships)) {
    return { column_key: column.key, kind: 'out_of_day', editable: false };
  }

  const aggregateDay = resolveAggregateDay(column.key);
  const weekDay = weekDayByAggregateDay.get(aggregateDay);
  if (!weekDay?.register_id) {
    return { column_key: column.key, kind: 'blocked', editable: false };
  }

  const dayCells = cellsByAggregateDay.get(aggregateDay) ?? [];
  const value = toGridCellValue({
    justification: dayCells.find((cell) => cell.value.justification)?.value.justification ?? null,
    absence_request:
      dayCells.find((cell) => cell.value.absence_request)?.value.absence_request ?? null,
    attendance_entries: dayCells.flatMap((cell) => cellEntries(cell.value)),
  });

  return {
    column_key: column.key,
    kind: 'normal',
    editable: !isFuture && hasSessionEntry(value),
    data: {
      register_id: weekDay.register_id,
      date: column.key,
      relation_id: member.relation_id,
      cell: value,
    },
  };
};

const buildMemberCells = (
  member: GroupGridMember,
  columns: AttendanceGridColumn[],
  weekDayByAggregateDay: Map<number, AggregateWeekDay>,
  rowByRelationId: Map<string, AggregateRow>,
): AttendanceGridCell[] => {
  const cellsByAggregateDay = new Map<number, AggregateCell[]>();
  for (const cell of sessionCells(rowByRelationId.get(member.relation_id)?.cells)) {
    const dayCells = cellsByAggregateDay.get(cell.meta.day) ?? [];
    dayCells.push(cell);
    cellsByAggregateDay.set(cell.meta.day, dayCells);
  }

  return columns.map((column) =>
    buildDayCell(column, member, weekDayByAggregateDay, cellsByAggregateDay),
  );
};

export const buildGroupGridFromAggregate = ({
  weekDates,
  today,
  members,
  aggregate,
}: BuildGroupGridInput): GroupGridResult => {
  const weekDayByAggregateDay = indexWeekDaysByDay(aggregate);
  const sessionNamesByDay = indexSessionNamesByDay(aggregate);
  const rowByRelationId = indexRowsByRelationId(aggregate);

  const columns = buildColumns(weekDates, today, weekDayByAggregateDay, sessionNamesByDay);

  const weekStart = parseDate(weekDates[0]!);
  const weekEnd = parseDate(weekDates[weekDates.length - 1]!);

  const rows = members
    .filter((member) => membershipOverlapsWeek(member, weekStart, weekEnd))
    .sort(compareMembers)
    .map((member) => ({
      relation_id: member.relation_id,
      student: member,
      cells: buildMemberCells(member, columns, weekDayByAggregateDay, rowByRelationId),
    }));

  return { columns, rows };
};

export const buildGroupSkeleton = (
  members: GroupGridMember[],
  weekDates: string[],
): GroupGridResult => {
  const columns: AttendanceGridColumn[] = Array.from(
    { length: WORKING_WEEK_LENGTH },
    (_, index) => ({
      key: `skeleton-${index}`,
      date: null,
      label: '',
      sessions: [],
      has_register: false,
      is_future: false,
      is_loading: true,
    }),
  );

  const weekStart = parseDate(weekDates[0]!);
  const weekEnd = parseDate(weekDates[weekDates.length - 1]!);

  const rows = members
    .filter((member) => membershipOverlapsWeek(member, weekStart, weekEnd))
    .sort(compareMembers)
    .map((member) => ({
      relation_id: member.relation_id,
      student: member,
      cells: columns.map<AttendanceGridCell>((column) => ({
        column_key: column.key,
        kind: 'loading' as const,
        editable: false,
      })),
    }));

  return { columns, rows };
};
